<template>
  <component
    :is="filterComponent"
    v-bind="filterProps"
    :model-value="value"
		:class="{ 'filter-active': isActive }"
    @update:model-value="$emit('update:value', $event)"
  />
</template>

<script lang="ts">
import VInput from '@/lib/components/VInput/VInput.vue';
import VInputNumber from '@/lib/components/VInputNumber/VInputNumber.vue';
import VInputMask from '@/lib/components/VInputMask/VInputMask.vue';
import VSelect from '@/lib/components/VSelect/VSelect.vue';
import VSelectV2 from '@/lib/components/VSelectV2/VSelectV2.vue';
import VDateRangeInput from "@/lib/components/VDateRangeInput/VDateRangeInput.vue";
import type { DataGridColumn } from './VDataGridColumnV2.vue';

const FILTER_COMPONENTS: Record<string, Component> = {
  text: VInput,
  number: VInputNumber,
  mask: VInputMask,
  select: VSelect,
  'date-range': VDateRangeInput,
};

/** Ширина колонки бывает узкой — попап селекта не уже этого значения, px */
const SELECT_POPUP_MIN_WIDTH: number = 300;

export default defineComponent({
  name: 'VDataGridFilterV2',

  props: {
    column: {
      type: Object as PropType<DataGridColumn>,
      required: true,
    },
    value: {
      type: [String, Number, Object, Array] as PropType<unknown>,
      default: null,
    },
  },

  emits: ['update:value'],

  computed: {
    filterComponent(): Component {
      /** Колонки с источником data (лоадер или функция) переезжают на VSelectV2 по одной, остальные — на старом VSelect */
      if (this.column.filterType === 'select' && this.$core.utils.isNullOrUndefined(this.column.filterProps?.data) === false) {
        return VSelectV2;
      }
      else {
        return FILTER_COMPONENTS[this.column.filterType] || VInput;
      }
    },

    filterProps(): Record<string, unknown> {
      const col = this.column;
      const props = col.filterProps || {};

      const base: Record<string, unknown> = {
        textAlign:       col.contentAlign,
        clearable:       props.clearable !== false,
        disabled:        props.disabled === true,
        disabledReasons: props.disabledReasons ?? null,
      };

      switch (col.filterType) {
        case 'text':
          return {
            ...base,
            placeholder: props.placeholder || '',
          };

        case 'number':
          return {
            ...base,
            min: props.min,
            max: props.max,
            step: props.step,
            precision: props.precision,
          };

        case 'mask':
          return {
            ...base,
            mask: props.mask,
            masked: props.masked,
            tokens: props.tokens,
          };

        case 'select':
          if (this.$core.utils.isNullOrUndefined(props.data) === false) {
            return {
              placeholder:     props.placeholder || 'Все',
              data:            props.data,
              multiple:        props.multiple === true,
              hideClearBtn:    props.clearable === false,
              disabled:        props.disabled === true,
              disabledReasons: props.disabledReasons ?? null,
              popupMinWidth:   SELECT_POPUP_MIN_WIDTH,
            };
          }

          return {
            ...base,
            placeholder: 				props.placeholder || 'Все',
            searchable: 				props.searchable,
            multiple: 					props.multiple,
            options: 						props.options,
            optionLabel: 				props.optionLabel,
            optionIdentity: 		props.optionIdentity,
            dataUrl: 						props.dataUrl,
            minimumInputLength: props.minimumInputLength,
						searchFunction:     props.searchFunction,
            popupMinWidth:      SELECT_POPUP_MIN_WIDTH,
          };

        case 'date-range':
          return {
            ...base,
            placeholder: props.placeholder,
            showMonths: props.showMonths,
          };

        default:
          return base;
      }
    },

		isActive(): boolean {
			const v = this.value;
			if (v === null || v === undefined || v === '') return false;
			if (Array.isArray(v)) return v.length > 0;
			return true;
		},
  },
});
</script>
